/**
 * The four page shapes (Feature 70, §6). Every routed page inside the shell
 * is one of them, and the shape, not the page, decides the measure and the
 * header:
 *
 *   ListShape    a collection and its toolbar      page measure
 *   RecordShape  one entity, in the detail frame   reading measure
 *   FormShape    a single-column form              form measure (page with `wide`)
 *   BoardShape   the Overview board                reading measure
 *
 * A header holds at most one primary `action` and an overflow `menu`; a
 * secondary action belongs on a row, in the record panel, or in the menu.
 * Forms and the board take no header actions: a form's actions are at its
 * foot, the board's are on its rows. A stepped form is finixt-ui's
 * WizardLayout, used directly. A page that needs a fifth shape says why in a
 * comment beside it.
 *
 * The body is the page's own; a shape takes it as children. ShapeLoading and
 * ShapeError give a page's early return the same frame without a layout
 * import.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ErrorBlock, LoadingBlock } from './AsyncState.js';
import { ContentLayout, type ContentVariant } from './ContentLayout.js';
import { Dropdown, DropdownItem } from './Dropdown.js';
import { DetailHeader, Icon, IconButton, Inline } from './finixt.js';
import { PageHeader } from './PageHeader.js';

export interface ShapeMenuItem {
  icon: string;
  label: string;
  sub?: string | undefined;
  tone?: 'default' | 'danger' | undefined;
  disabled?: boolean | undefined;
  onSelect: () => void;
}

interface HeaderActions {
  /** The one primary action. */
  action?: ReactNode;
  /** Everything else the page offers from its header, behind "More actions". */
  menu?: readonly ShapeMenuItem[] | undefined;
}

/** The action and the overflow menu, in that order; nothing when both are empty. */
function ShapeActions({ action, menu }: HeaderActions) {
  const items = (menu ?? []).filter(Boolean);
  if (!action && items.length === 0) return null;
  return (
    <Inline wrap={false} align="center" gap={7}>
      {action}
      {items.length > 0 ? (
        <Dropdown
          align="end"
          trigger={<IconButton icon="more_horiz" size={34} aria-label="More actions" />}
        >
          {items.map((item) => (
            <DropdownItem
              key={item.label}
              icon={item.icon}
              label={item.label}
              sub={item.sub}
              tone={item.tone}
              disabled={item.disabled}
              onSelect={item.onSelect}
            />
          ))}
        </Dropdown>
      ) : null}
    </Inline>
  );
}

export interface ListShapeProps extends HeaderActions {
  /** Contextual kicker, such as a date or a period. Never the nav section. */
  kicker?: string | undefined;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}

export function ListShape({ kicker, title, subtitle, action, menu, children }: ListShapeProps) {
  return (
    <ContentLayout variant="page">
      <PageHeader
        overline={kicker}
        title={title}
        subtitle={subtitle}
        actions={<ShapeActions action={action} menu={menu} />}
      />
      {children}
    </ContentLayout>
  );
}

export interface RecordShapeProps extends HeaderActions {
  /** Where the record sits: the list it came from. */
  back?: { to: string; label: string } | undefined;
  avatar?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  /** Docked beside the column, outside its scroll. */
  rail?: ReactNode;
  children: ReactNode;
}

export function RecordShape({
  back,
  avatar,
  eyebrow,
  title,
  subtitle,
  meta,
  action,
  menu,
  rail,
  children,
}: RecordShapeProps) {
  return (
    <ContentLayout
      detail={{
        back: back ? (
          <Link
            to={back.to}
            style={{
              marginBottom: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 'var(--text-body-md)',
              color: 'var(--text-muted)',
            }}
          >
            <Icon name="arrow_back" size={15} />
            {back.label}
          </Link>
        ) : undefined,
        header: (
          <DetailHeader
            avatar={avatar}
            eyebrow={eyebrow}
            title={title}
            subtitle={subtitle}
            meta={meta}
            actions={<ShapeActions action={action} menu={menu} />}
          />
        ),
        rail,
      }}
    >
      {children}
    </ContentLayout>
  );
}

export interface FormShapeProps {
  kicker?: string | undefined;
  title: string;
  subtitle?: ReactNode;
  /** A form with its result beside it (Sandbox): the page measure. */
  wide?: boolean | undefined;
  children: ReactNode;
}

export function FormShape({ kicker, title, subtitle, wide = false, children }: FormShapeProps) {
  return (
    <ContentLayout variant={wide ? 'page' : 'form'}>
      <PageHeader overline={kicker} title={title} subtitle={subtitle} />
      {children}
    </ContentLayout>
  );
}

export interface BoardShapeProps {
  kicker?: string | undefined;
  /** The board states its shape, so it is a sentence, not a name. */
  headline: string;
  subtitle?: ReactNode;
  children: ReactNode;
}

export function BoardShape({ kicker, headline, subtitle, children }: BoardShapeProps) {
  return (
    <ContentLayout variant="reading">
      <PageHeader overline={kicker} title={headline} subtitle={subtitle} />
      {children}
    </ContentLayout>
  );
}

const SHAPE_MEASURE: Record<'list' | 'record' | 'form' | 'board', ContentVariant> = {
  list: 'page',
  record: 'reading',
  form: 'form',
  board: 'reading',
};

/** A page still loading, in its shape's measure. */
export function ShapeLoading({ shape }: { shape: keyof typeof SHAPE_MEASURE }) {
  return (
    <ContentLayout variant={SHAPE_MEASURE[shape]}>
      <LoadingBlock />
    </ContentLayout>
  );
}

/** A page that could not load, in its shape's measure. */
export function ShapeError({
  shape,
  error,
  fallback = 'Failed to load',
}: {
  shape: keyof typeof SHAPE_MEASURE;
  error: unknown;
  fallback?: string;
}) {
  return (
    <ContentLayout variant={SHAPE_MEASURE[shape]}>
      <ErrorBlock message={error instanceof Error ? error.message : fallback} />
    </ContentLayout>
  );
}
